import { type RefObject, useLayoutEffect, useState } from 'react';

type Params = {
  listRef: RefObject<HTMLElement | null>;
  moreRef: RefObject<HTMLElement | null>;
  isEnabled: boolean;
  childrenKey: string;
};

const TRIGGER_SELECTOR = ':scope > [data-slot=tabs-trigger]';

export const useTabsOverflow = (params: Params) => {
  const { listRef, moreRef, isEnabled, childrenKey } = params;
  const [hiddenValues, setHiddenValues] = useState<string[]>([]);

  useLayoutEffect(() => {
    const list = listRef.current;
    const more = moreRef.current;
    if (!isEnabled || !list || !more) {
      return;
    }

    const getTriggers = () => [
      ...list.querySelectorAll<HTMLElement>(TRIGGER_SELECTOR),
    ];

    const updateOverflow = () => {
      const triggers = getTriggers();
      triggers.forEach((trigger) => delete trigger.dataset.overflow);

      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const widths = triggers.map((trigger) => trigger.offsetWidth);
      const totalWidth = widths.reduce(
        (sum, width, index) => sum + width + (index > 0 ? gap : 0),
        0,
      );

      let visibleCount = triggers.length;
      if (totalWidth > list.clientWidth) {
        let usedWidth = 0;
        visibleCount = 0;
        for (const [index, width] of widths.entries()) {
          const nextWidth = usedWidth + (index > 0 ? gap : 0) + width;
          if (nextWidth + gap + more.offsetWidth > list.clientWidth) {
            break;
          }
          usedWidth = nextWidth;
          visibleCount = index + 1;
        }
      }

      const hiddenTriggers = triggers.slice(visibleCount);
      hiddenTriggers.forEach((trigger) => {
        trigger.dataset.overflow = 'true';
      });
      const nextHiddenValues = hiddenTriggers.map(
        (trigger) => trigger.dataset.value ?? '',
      );
      setHiddenValues((prevHiddenValues) =>
        prevHiddenValues.join('|') === nextHiddenValues.join('|')
          ? prevHiddenValues
          : nextHiddenValues,
      );
    };

    updateOverflow();
    const observer = new ResizeObserver(updateOverflow);
    observer.observe(list);
    getTriggers().forEach((trigger) => observer.observe(trigger));
    return () => observer.disconnect();
  }, [listRef, moreRef, isEnabled, childrenKey]);

  return hiddenValues;
};
